import { useState, useRef } from "react";
import { api, API_BASE, formatApiError, extractFieldErrors } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Upload, Download, FileSpreadsheet, AlertCircle, CheckCircle2, ArrowLeft, ArrowRight } from "lucide-react";

const STRATEGY_LABEL = {
  skip: "Skip duplicates",
  update: "Update duplicates",
  create_new: "Create new (allow duplicates)",
};

const ACTION_STYLE = {
  create: "bg-emerald-500/15 text-emerald-700 border-emerald-500/30",
  update: "bg-amber-500/15 text-amber-700 border-amber-500/30",
  skip: "bg-slate-500/15 text-slate-600 border-slate-500/30",
  error: "bg-rose-500/15 text-rose-700 border-rose-500/30",
};

function StepIndicator({ step }) {
  const steps = ["Upload", "Review", "Done"];
  return (
    <div className="flex items-center gap-2 mb-4" data-testid="import-wizard-steps">
      {steps.map((s, i) => {
        const n = i + 1;
        const active = step === n;
        const done = step > n;
        return (
          <div key={s} className="flex items-center gap-2">
            <div
              className={`h-7 w-7 rounded-full grid place-items-center text-[11px] font-bold border ${
                done ? "bg-accent text-accent-foreground border-accent"
                : active ? "bg-foreground text-background border-foreground"
                : "bg-secondary text-muted-foreground border-border"
              }`}
            >
              {done ? <CheckCircle2 className="h-4 w-4" /> : n}
            </div>
            <span className={`text-xs uppercase tracking-wider ${active ? "font-bold" : "text-muted-foreground"}`}>{s}</span>
            {n < steps.length && <div className="w-6 h-px bg-border" />}
          </div>
        );
      })}
    </div>
  );
}

async function downloadTemplate(format) {
  // Authenticated download via fetch + blob (axios returns JSON by default).
  const token = localStorage.getItem("jb_token");
  try {
    const res = await fetch(`${API_BASE}/customers/import-template?format=${format}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = format === "csv" ? "customers_import_template.csv" : "customers_import_template.xlsx";
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  } catch (e) {
    toast.error(`Template download failed: ${e?.message || e}`);
  }
}

// Phase 30 — Customer export companion to the import wizard. Exposed so the
// Customers list page can hook into the same auth-aware blob-download flow.
export async function downloadCustomerExport(format) {
  const token = localStorage.getItem("jb_token");
  try {
    const res = await fetch(`${API_BASE}/customers/export?format=${format}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) {
      let detail = `HTTP ${res.status}`;
      try {
        const j = await res.json();
        if (j?.detail) detail = typeof j.detail === "string" ? j.detail : JSON.stringify(j.detail);
      } catch { /* ignore */ }
      throw new Error(detail);
    }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = format === "csv" ? "customers_export.csv" : "customers_export.xlsx";
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    toast.success(`Customer export ready (${format})`);
  } catch (e) {
    toast.error(`Export failed: ${e?.message || e}`);
  }
}

export default function CustomerImportWizard({ open, onOpenChange, onCompleted }) {
  const [step, setStep] = useState(1);
  const [file, setFile] = useState(null);
  const [strategy, setStrategy] = useState("skip");
  const [uploading, setUploading] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const fileInputRef = useRef(null);

  const reset = () => {
    setStep(1);
    setFile(null);
    setStrategy("skip");
    setUploading(false);
    setCommitting(false);
    setPreview(null);
    setResult(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleClose = (next) => {
    if (!next) reset();
    onOpenChange?.(next);
  };

  const onPickFile = (e) => {
    const f = e.target.files?.[0] || null;
    if (f) {
      const lower = f.name.toLowerCase();
      if (!(lower.endsWith(".csv") || lower.endsWith(".xlsx"))) {
        toast.error("Only .csv or .xlsx files are supported");
        return;
      }
    }
    setFile(f);
  };

  const onUpload = async () => {
    if (!file) {
      toast.error("Please pick a file first");
      return;
    }
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("duplicate_strategy", strategy);
      const { data } = await api.post("/customers/import/preview", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreview(data);
      setStep(2);
    } catch (e) {
      // Phase 48 — show clean toast string; 422 detail array no longer crashes.
      const { generalMessage } = extractFieldErrors(e);
      toast.error(generalMessage || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const onCommit = async () => {
    if (!preview) return;
    setCommitting(true);
    try {
      const { data } = await api.post("/customers/import/commit", {
        preview_token: preview.preview_token,
        duplicate_strategy: strategy,
      });
      setResult(data);
      setStep(3);
      onCompleted?.();
    } catch (e) {
      const { generalMessage } = extractFieldErrors(e);
      toast.error(generalMessage || "Commit failed");
    } finally {
      setCommitting(false);
    }
  };

  // Re-preview when strategy changes after the initial upload so the
  // counts on screen stay accurate without forcing a re-upload.
  const onStrategyChange = async (newStrategy) => {
    setStrategy(newStrategy);
    if (step === 2 && file) {
      setUploading(true);
      try {
        const form = new FormData();
        form.append("file", file);
        form.append("duplicate_strategy", newStrategy);
        const { data } = await api.post("/customers/import/preview", form, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setPreview(data);
      } catch (e) {
        toast.error(formatApiError(e?.response?.data?.detail));
      } finally {
        setUploading(false);
      }
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        className="max-w-3xl w-[95vw] max-h-[92vh] overflow-y-auto"
        data-testid="import-wizard-dialog"
      >
        <DialogHeader>
          <DialogTitle>Import customers</DialogTitle>
          <DialogDescription>
            Upload an Excel or CSV file to bulk-create or update customers.
          </DialogDescription>
        </DialogHeader>

        <StepIndicator step={step} />

        {step === 1 && (
          <div className="space-y-5" data-testid="import-step-upload">
            <div className="bg-secondary/50 border border-border rounded-[8px] p-4 space-y-3">
              <div className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">
                1. Download a template
              </div>
              <div className="text-sm text-muted-foreground">
                Fill in your customers — one per row. The first row is the header.
                Use <code className="text-foreground">;</code> to separate multiple phones or emails.
              </div>
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  variant="outline"
                  className="gap-2"
                  onClick={() => downloadTemplate("xlsx")}
                  data-testid="download-xlsx-template"
                >
                  <FileSpreadsheet className="h-4 w-4" /> Excel (.xlsx)
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  className="gap-2"
                  onClick={() => downloadTemplate("csv")}
                  data-testid="download-csv-template"
                >
                  <Download className="h-4 w-4" /> CSV (.csv)
                </Button>
              </div>
            </div>

            <div className="border border-dashed border-border rounded-[8px] p-5 space-y-3">
              <div className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">
                2. Upload your file
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv"
                onChange={onPickFile}
                className="block w-full text-sm file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-foreground file:text-background hover:file:bg-foreground/90"
                data-testid="import-file-input"
              />
              {file && (
                <div className="text-xs text-muted-foreground" data-testid="import-file-name">
                  Selected: <span className="text-foreground font-semibold">{file.name}</span> ({Math.round(file.size / 1024)} KB)
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wider font-semibold">
                If a customer already exists
              </Label>
              <Select value={strategy} onValueChange={setStrategy}>
                <SelectTrigger data-testid="import-strategy-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="skip" data-testid="import-strategy-skip">Skip — leave existing untouched</SelectItem>
                  <SelectItem value="update" data-testid="import-strategy-update">Update — overwrite existing fields</SelectItem>
                  <SelectItem value="create_new" data-testid="import-strategy-create_new">Create new — allow duplicates</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        )}

        {step === 2 && preview && (
          <div className="space-y-4" data-testid="import-step-review">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <div className="bg-card border border-border rounded-[8px] p-3" data-testid="import-summary-total">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Total</div>
                <div className="text-2xl font-black">{preview.total_rows}</div>
              </div>
              <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-[8px] p-3" data-testid="import-summary-create">
                <div className="text-[10px] uppercase tracking-wider text-emerald-700">Will create</div>
                <div className="text-2xl font-black text-emerald-700">{preview.will_create}</div>
              </div>
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-[8px] p-3" data-testid="import-summary-update">
                <div className="text-[10px] uppercase tracking-wider text-amber-700">Will update</div>
                <div className="text-2xl font-black text-amber-700">{preview.will_update}</div>
              </div>
              <div className="bg-rose-500/10 border border-rose-500/30 rounded-[8px] p-3" data-testid="import-summary-errors">
                <div className="text-[10px] uppercase tracking-wider text-rose-700">Errors</div>
                <div className="text-2xl font-black text-rose-700">{preview.error_rows}</div>
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wider font-semibold">
                Duplicate strategy
              </Label>
              <Select value={strategy} onValueChange={onStrategyChange}>
                <SelectTrigger data-testid="import-strategy-select-review"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="skip">Skip — leave existing untouched</SelectItem>
                  <SelectItem value="update">Update — overwrite existing fields</SelectItem>
                  <SelectItem value="create_new">Create new — allow duplicates</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="border border-border rounded-[8px] overflow-hidden">
              <div className="max-h-[40vh] overflow-y-auto">
                <table className="w-full text-sm" data-testid="import-preview-table">
                  <thead className="sticky top-0 bg-secondary/80 backdrop-blur">
                    <tr className="text-left">
                      <th className="py-2 px-3 text-[10px] uppercase tracking-wider">#</th>
                      <th className="py-2 px-3 text-[10px] uppercase tracking-wider">Action</th>
                      <th className="py-2 px-3 text-[10px] uppercase tracking-wider">Company</th>
                      <th className="py-2 px-3 text-[10px] uppercase tracking-wider">Contact</th>
                      <th className="py-2 px-3 text-[10px] uppercase tracking-wider">Phones / Emails</th>
                      <th className="py-2 px-3 text-[10px] uppercase tracking-wider">Notes</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {preview.rows.map((r) => (
                      <tr key={r.row_index} data-testid={`import-preview-row-${r.row_index}`}>
                        <td className="py-2 px-3 text-muted-foreground tabular-nums">{r.row_index}</td>
                        <td className="py-2 px-3">
                          <span className={`inline-block text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border ${ACTION_STYLE[r.action]}`}>
                            {r.action}
                          </span>
                        </td>
                        <td className="py-2 px-3">
                          <div className="font-semibold">{r.company_name || <span className="text-muted-foreground italic">(empty)</span>}</div>
                          {r.existing_company_name && r.existing_company_name !== r.company_name && (
                            <div className="text-[11px] text-muted-foreground">matches: {r.existing_company_name}</div>
                          )}
                        </td>
                        <td className="py-2 px-3 text-muted-foreground">{r.contact_name}</td>
                        <td className="py-2 px-3 text-xs">
                          {r.phones?.length > 0 && (
                            <div className="truncate max-w-[220px]">{r.phones.join(", ")}</div>
                          )}
                          {r.emails?.length > 0 && (
                            <div className="truncate max-w-[220px] text-muted-foreground">
                              {r.emails.map((e) => e.email).join(", ")}
                            </div>
                          )}
                          {r.errors?.length > 0 && (
                            <div className="mt-1 text-rose-600 flex items-start gap-1">
                              <AlertCircle className="h-3 w-3 mt-0.5 shrink-0" />
                              <span>{r.errors.map((er) => er.message).join("; ")}</span>
                            </div>
                          )}
                        </td>
                        <td className="py-2 px-3 text-xs text-muted-foreground max-w-[160px] truncate">{r.notes}</td>
                      </tr>
                    ))}
                    {preview.rows.length === 0 && (
                      <tr><td colSpan={6} className="py-6 text-center text-muted-foreground text-sm">No rows found in the file.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="text-xs text-muted-foreground">
              Strategy: <span className="font-semibold text-foreground">{STRATEGY_LABEL[strategy]}</span>. Rows with errors are skipped on commit.
            </div>
          </div>
        )}

        {step === 3 && result && (
          <div className="space-y-4" data-testid="import-step-done">
            <div className="flex items-start gap-3 bg-emerald-500/10 border border-emerald-500/30 rounded-[8px] p-4">
              <CheckCircle2 className="h-6 w-6 text-emerald-600 shrink-0 mt-0.5" />
              <div>
                <div className="font-bold text-base">Import complete</div>
                <div className="text-sm text-muted-foreground">
                  Customers are now in your list and ready to use.
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <div className="bg-card border border-border rounded-[8px] p-3" data-testid="import-result-created">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Created</div>
                <div className="text-2xl font-black text-emerald-700">{result.created}</div>
              </div>
              <div className="bg-card border border-border rounded-[8px] p-3" data-testid="import-result-updated">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Updated</div>
                <div className="text-2xl font-black text-amber-700">{result.updated}</div>
              </div>
              <div className="bg-card border border-border rounded-[8px] p-3" data-testid="import-result-skipped">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Skipped</div>
                <div className="text-2xl font-black">{result.skipped}</div>
              </div>
              <div className="bg-card border border-border rounded-[8px] p-3" data-testid="import-result-errors">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Errors</div>
                <div className="text-2xl font-black text-rose-700">{result.errors}</div>
              </div>
            </div>
          </div>
        )}

        <DialogFooter className="flex-row justify-between gap-2">
          {step === 1 && (
            <>
              <Button variant="ghost" onClick={() => handleClose(false)} data-testid="import-cancel-button">Cancel</Button>
              <Button
                onClick={onUpload}
                disabled={!file || uploading}
                className="btn-cta gap-2"
                data-testid="import-upload-button"
              >
                <Upload className="h-4 w-4" />
                {uploading ? "Uploading..." : "Upload & preview"}
              </Button>
            </>
          )}
          {step === 2 && (
            <>
              <Button variant="ghost" onClick={() => { setStep(1); setPreview(null); }} className="gap-2" data-testid="import-back-button">
                <ArrowLeft className="h-4 w-4" /> Back
              </Button>
              <Button
                onClick={onCommit}
                disabled={committing || uploading || !preview || (preview.will_create + preview.will_update === 0)}
                className="btn-cta gap-2"
                data-testid="import-commit-button"
              >
                {committing ? "Importing..." : (
                  <>Confirm import <ArrowRight className="h-4 w-4" /></>
                )}
              </Button>
            </>
          )}
          {step === 3 && (
            <>
              <Button variant="outline" onClick={reset} data-testid="import-another-button">Import another file</Button>
              <Button onClick={() => handleClose(false)} className="btn-cta" data-testid="import-done-button">Done</Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
